import { Component, ComponentType, ErrorInfo, PropsWithChildren, ReactNode } from "react";
import { Alert, AlertTitle, Box } from "@mui/material";

interface IFallbackProps {
    error: Error;
}

interface IErrorBoundaryProps {
    onError?: (error: Error, info: { componentStack: string }) => void;
    FallbackComponent?: ComponentType<IFallbackProps>;
}

interface IErrorBoundaryState {
    error: Error | null;
}

/**
 * Error Boundary component -- wrap other components in an ErrorBoundary component
 * to prevent uncaught exceptions in child components from wrecking react page-wide.
 *
 * Optionally pass FallbackComponent for a replacement node in case of error.
 * If no error is caught, the children are rendered normally.
 */
export class ErrorBoundary extends Component<PropsWithChildren<IErrorBoundaryProps>, IErrorBoundaryState> {
    static getDerivedStateFromError(error: Error): IErrorBoundaryState {
        return { error };
    }

    state: IErrorBoundaryState = { error: null };

    componentDidCatch(error: Error, info: ErrorInfo): void {
        const { onError } = this.props;
        onError?.(error, info);
    }

    render(): ReactNode {
        const { error } = this.state;
        const { FallbackComponent, children } = this.props;

        if (error !== null) {
            if (FallbackComponent) {
                return <FallbackComponent error={error} />;
            }

            if (process.env.NODE_ENV === "development") {
                return (
                    <Alert severity="error">
                        <AlertTitle>Something went wrong!</AlertTitle>
                        <Box component="pre" sx={{ whiteSpace: "pre-wrap" }}>
                            {error.stack}
                        </Box>
                    </Alert>
                );
            }

            return null;
        }

        return children;
    }
}
